<template>
    <div>
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-3xl font-bold text-gray-900 mb-2">
                    Announcements
                </h2>
                <p>Manage the service announcement banner and send notification emails to all registered users.</p>
            </div>
        </div>

        <!-- Banner Settings Section -->
        <div class="bg-white shadow rounded-lg p-6 mb-8">
            <h3 class="text-xl font-bold text-gray-800 mb-4">Service Announcement Banner</h3>
            <p class="text-gray-600 mb-6">
                When enabled, this banner is displayed at the top of the home page for all visitors.
            </p>

            <div class="mb-4">
                <label class="flex items-center gap-3 cursor-pointer mb-4">
                    <d-switch v-model="bannerEnabled" />
                    <span class="font-semibold">{{ bannerEnabled ? 'Banner Enabled' : 'Banner Disabled' }}</span>
                </label>
            </div>

            <d-field label="Banner Message">
                <l-html-editor v-model="bannerText" />
            </d-field>

            <div class="mt-4 flex gap-3">
                <d-btn
                    color="primary"
                    :loading="savingBanner"
                    @click="saveBannerSettings"
                >
                    Save Banner Settings
                </d-btn>
            </div>
        </div>

        <!-- Send Announcement Email Section -->
        <div class="bg-white shadow rounded-lg p-6">
            <h3 class="text-xl font-bold text-gray-800 mb-4">Send Announcement Email</h3>
            <p class="text-gray-600 mb-6">
                Compose and send an email to all registered users (promo-code accounts are excluded).
            </p>

            <d-field label="Subject" class="mb-4">
                <d-input
                    v-model="emailSubject"
                    placeholder="e.g. Service Disruption Notice"
                />
            </d-field>

            <d-field label="Message Body">
                <l-html-editor v-model="emailBody" />
            </d-field>

            <div class="mt-4 flex gap-3 items-center">
                <d-btn
                    color="secondary"
                    :loading="sendingEmail"
                    :disabled="!emailSubject || !emailBody"
                    @click="openSendConfirmModal"
                >
                    Send to All Registered Users
                </d-btn>
                <span v-if="lastSentCount !== null" class="text-sm text-gray-500">
                    Last send: {{ lastSentCount }} email(s) queued.
                </span>
            </div>
        </div>

        <!-- Send Confirmation Modal -->
        <d-modal :active="sendConfirmModalActive" :width="450" :can-cancel="['escape', 'x', 'outside']" @close="closeSendConfirmModal">
            <div class="p-6">
                <h3 class="text-lg font-bold text-gray-900 mb-4">Confirm Send</h3>
                <p class="text-gray-700 mb-3">
                    You are about to send an announcement email to all registered users.
                    This action cannot be undone. Are you sure you want to proceed?
                </p>
                <div class="p-3 bg-gray-50 rounded text-sm mb-6">
                    <strong>Subject:</strong> {{ emailSubject }}
                </div>
                <div class="flex gap-3 justify-end">
                    <d-btn flat @click="closeSendConfirmModal">Cancel</d-btn>
                    <d-btn color="secondary" :loading="sendingEmail" @click="sendAnnouncementEmail">Send Email</d-btn>
                </div>
            </div>
        </d-modal>
    </div>
</template>

<script>
import axios from 'axios';
import { toast } from '@services/toast.service';

export default {
    name: 'AnnouncementsPage',

    data() {
        return {
            bannerEnabled: false,
            bannerText: '',
            savingBanner: false,
            emailSubject: '',
            emailBody: '',
            sendingEmail: false,
            lastSentCount: null,
            sendConfirmModalActive: false,
        };
    },

    mounted() {
        this.loadBannerSettings();
    },

    methods: {
        async loadBannerSettings() {
            try {
                const { data } = await axios.get('/api/admin/announcements/banner');
                this.bannerEnabled = data.announcementBannerEnabled;
                this.bannerText = data.announcementBannerText || '';
            } catch (error) {
                console.error('Error loading banner settings:', error);
                toast.error('Failed to load banner settings.');
            }
        },

        async saveBannerSettings() {
            this.savingBanner = true;
            try {
                await axios.put('/api/admin/announcements/banner', {
                    announcementBannerEnabled: this.bannerEnabled,
                    announcementBannerText: this.bannerText,
                });
                toast.success('Banner settings saved successfully.');
            } catch (error) {
                console.error('Error saving banner settings:', error);
                toast.error('Failed to save banner settings.');
            } finally {
                this.savingBanner = false;
            }
        },

        openSendConfirmModal() {
            this.sendConfirmModalActive = true;
        },

        closeSendConfirmModal() {
            this.sendConfirmModalActive = false;
        },

        async sendAnnouncementEmail() {
            this.sendingEmail = true;
            try {
                const { data } = await axios.post('/api/admin/announcements/send-email', {
                    subject: this.emailSubject,
                    htmlBody: this.emailBody,
                });
                this.lastSentCount = data.recipientCount;
                toast.success(`Announcement email queued for ${data.recipientCount} recipient(s).`);
                this.closeSendConfirmModal();
                this.emailSubject = '';
                this.emailBody = '';
            } catch (error) {
                console.error('Error sending announcement email:', error);
                toast.error('Failed to send announcement email.');
            } finally {
                this.sendingEmail = false;
            }
        },
    },
};
</script>
